import * as React from "react";
import { UitkSecondaryButton } from "uitk-react-button";
import { AnalyticsConfig } from "uitk-react-utils";
import { useLocalization } from "@shared-ui/localization-context";
import { DestinationAttractionsProps } from "./typings";
import { ItemKeyHelper } from "components/utility/ItemKeyHelper";
import { Action, FlexTrackingInfo, sendDelayedTrackEvent } from "components/utility/analytics/FlexAnalyticsUtils";
import { AttractionSection } from "./AttractionSection";
import { observer } from "mobx-react";
import { withStores } from "src/stores";
import { DestinationAttractionsFlexModuleResult } from "src/typings/microserviceModels/destination-attractions-flex-module";

export const DestinationAttractions = withStores(
  "flexModuleModelStore",
  "context",
  "analytics"
)(
  observer((props: DestinationAttractionsProps) => {
    const { flexTemplateNode, flexModuleModelStore, context, analytics } = props;
    const { id, options } = flexTemplateNode;
    const model = flexModuleModelStore.getModel(id) as DestinationAttractionsFlexModuleResult | null;

    if (!model) {
      return null;
    }

    const content = model.content;
    const attractionsKeyHelper = new ItemKeyHelper("top-attractions");
    const location = context.searchContext.location;
    const isCountry = location?.type === "country";
    const locationName = location?.localizedName || location?.name;
    let buttonLocationName = locationName;
    const locationType = location?.type;
    const regionId = location?.id;
    if (locationType === "neighborhood" || locationType === "point_of_interest") {
      buttonLocationName = location?.parent.localizedName;
    }
    const { formatText } = useLocalization();

    const pois = content?.pois.filter((attraction) => {
      return attraction.name && attraction.media?.mediaUrl?.length && attraction.description;
    });

    const activities = content?.activities.filter((attraction) => {
      return attraction.name && attraction.media?.mediaUrl?.length && attraction.description;
    });

    const activitiesTravelGuideUrl = content?.activitiesTravelGuideUrl;
    const showMaxAttractions = model.showMaxAttractions;
    const ctaButtonToSRP = model.ctaButtonToSRP;
    const seeAllActivitiesRedirectURL =
      ctaButtonToSRP && regionId ? `/things-to-do/search?rid=${regionId}` : activitiesTravelGuideUrl;

    /* istanbul ignore next */
    const carouselAnalytics: AnalyticsConfig = {
      id: "TG.LP.Dest.Attractions",
      callback: (moduleId, description) => {
        const flexTrackingInfo: FlexTrackingInfo = {
          moduleName: moduleId,
          action: Action.SCROLL,
          linkName: description,
        };
        sendDelayedTrackEvent(flexTrackingInfo, analytics);
      },
    };

    /* istanbul ignore next */
    const buttonAnalytics: AnalyticsConfig = {
      id: "TG.LP.Dest.Attractions.Activities.Button",
      callback: (moduleId, description) => {
        const flexTrackingInfo: FlexTrackingInfo = {
          moduleName: moduleId,
          action: Action.CLICK,
          linkName: description,
        };
        sendDelayedTrackEvent(flexTrackingInfo, analytics);
      },
    };

    return (
      <div
        className="DestinationAttractions uitk-cell all-cell-1-1"
        id={id}
        data-fm={options.fmId}
        data-fm-title-id={options.fmTitleId}
      >
        {pois && pois.length > 0 && (
          <AttractionSection
            attractions={pois}
            attractionsKeyHelper={attractionsKeyHelper}
            headingKey={
              isCountry ? "destinationAttractions.country.pois.title" : "destinationAttractions.city.pois.title"
            }
            carouselAnalytics={carouselAnalytics}
            isActivity={false}
            isCountry={isCountry}
            locationName={locationName}
            context={context}
            showMaxAttractions={showMaxAttractions}
          />
        )}
        {activities &&
          activities.length > 0 && ( // There are never activities for countries.
            <>
              <AttractionSection
                attractions={activities}
                attractionsKeyHelper={attractionsKeyHelper}
                headingKey="destinationAttractions.city.activities.title"
                carouselAnalytics={carouselAnalytics}
                isCountry={isCountry}
                isActivity={true}
                locationName={locationName}
                context={context}
                showMaxAttractions={showMaxAttractions}
              />
              {Boolean(seeAllActivitiesRedirectURL) && (
                <div className="uitk-type-center">
                  <UitkSecondaryButton
                    text={`${
                      Boolean(buttonLocationName) &&
                      formatText("destinationAttractions.city.activities.button.label", buttonLocationName)
                    }`}
                    href={seeAllActivitiesRedirectURL}
                    analytics={buttonAnalytics}
                  />
                </div>
              )}
            </>
          )}
      </div>
    );
  })
);

export default DestinationAttractions;
